import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../utils/cn';
import { Card } from '../Card';
import type { FeatureCardProps } from './FeatureCard.types';

/**
 * FeatureCard - Universal card component for consistent UI across the application.
 *
 * Refactored to use the standardized Card primitive.
 * Retains specific content layout (Label, Value, Progress) but delegates
 * visual effects (Glow, Watermark, Active State) to the Card component.
 */
export const FeatureCard: React.FC<FeatureCardProps> = ({
  label,
  title,
  value,
  unit,
  icon,
  color,
  statusColor,
  statusLabel,
  statusIcon,
  iconClassName: _iconClassName,
  onClick,
  isActive = false,
  className,
  children,
  headerAction,
  progress,
}) => {
  const isClickable = !!onClick;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
      className={cn('h-full', className)}
    >
      <Card
        variant="glass"
        padding="md"
        onClick={onClick}
        interactive={isClickable}
        selected={isActive}
        accentColor={color}
        // New Props
        glowColor={color}
        watermarkIcon={icon}
        // Pass class for height
        className="h-full"
      >
        <div className="flex flex-col h-full max-w-[85%] relative z-10">
          {/* Header: Label Badge & Status Badge */}
          <div className="flex justify-between items-start mb-4">
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-white/5 text-[11px] font-black uppercase tracking-widest text-gray-500 dark:text-zinc-400">
              {label}
            </span>

            {/* Status Badge - Custom implementation retained as it's specific to FeatureCard */}
            {statusLabel && (
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold backdrop-blur-sm border"
                style={{
                  color: statusColor || color,
                  backgroundColor: `${statusColor || color}15`,
                  borderColor: `${statusColor || color}40`,
                }}
              >
                {statusIcon && <span className="w-3 h-3">{statusIcon}</span>}
                {statusLabel}
              </div>
            )}

            {/* Header Action Slot */}
            {headerAction && <div className="ml-auto pl-2">{headerAction}</div>}
          </div>

          {/* Main Content */}
          <div className="flex-grow">
            <h3 className="text-lg font-medium text-gray-600 dark:text-zinc-300 mb-1">{title}</h3>

            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                {value}
              </span>
              {unit && (
                <span className="text-sm font-medium text-gray-500 dark:text-zinc-400 ml-1">
                  {unit}
                </span>
              )}
            </div>

            {children && <div className="mt-4">{children}</div>}
          </div>

          {/* Progress Bar */}
          {typeof progress === 'number' && (
            <div className="mt-6 w-full bg-gray-100 dark:bg-zinc-800 rounded-full h-1.5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                className="h-full rounded-full"
                style={{ backgroundColor: color }}
              />
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};
